import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type ViewState = 'loading' | 'ready' | 'error'

/**
 * Page wrapper. `data-view` names the page and `data-state` says whether it is loading, ready or
 * failed, so browser checks and Playwright wait for a real state instead of sleeping.
 */
export function View({
  name,
  state = 'ready',
  title,
  description,
  actions,
  children,
  className,
}: {
  name: string
  state?: ViewState
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <main
      data-view={name}
      data-state={state}
      aria-busy={state === 'loading'}
      className={cn(
        'mx-auto flex w-full max-w-3xl flex-1 flex-col gap-6 px-4 py-6 md:px-8',
        className,
      )}
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">{title}</h1>
          {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </header>
      {children}
    </main>
  )
}
